import React, { useState, useMemo } from 'react';
import { 
  ArrowLeft, Truck, DollarSign, Plus, Edit3, Trash2, X, Check, 
  AlertCircle, Save, MapPin, Package, Info, Globe, ChevronDown,
  Percent, Gift
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { 
  PRODUCTS, isModuleEnabledForOffice
} from '../data/database';

// Zone di spedizione predefinite
const DEFAULT_ZONES = [
  { id: 'local', name: 'Locale', description: 'Stessa città', color: 'emerald' },
  { id: 'regional', name: 'Regionale', description: 'Stessa regione', color: 'blue' },
  { id: 'national', name: 'Nazionale', description: 'Italia', color: 'purple' },
  { id: 'international', name: 'Internazionale', description: 'Europa', color: 'orange' },
];

const ShippingConfigPage = ({ onBack, onNavigate }) => {
  const { selectedOffice } = useApp();
  const officeId = selectedOffice?.id || 1;
  
  // Verifica se il modulo products è abilitato
  const hasProducts = isModuleEnabledForOffice(officeId, 'products');
  
  // Conta prodotti per questo office
  const productCount = useMemo(() => {
    return PRODUCTS.filter(p => p.officeId === officeId).length;
  }, [officeId]);
  
  // State
  const [shippingConfig, setShippingConfig] = useState({
    enableShipping: true,
    enablePickup: true,
    freeShippingThreshold: 50,
    handlingTime: 1,
    zones: [
      { id: 'local', name: 'Locale', price: 3.99, minDays: 1, maxDays: 2, enabled: true },
      { id: 'regional', name: 'Regionale', price: 5.99, minDays: 2, maxDays: 4, enabled: true },
      { id: 'national', name: 'Nazionale', price: 7.99, minDays: 3, maxDays: 5, enabled: true },
      { id: 'international', name: 'Europa', price: 15.99, minDays: 5, maxDays: 10, enabled: false },
    ],
  });
  
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [zoneForm, setZoneForm] = useState({
    id: '',
    name: '',
    price: '',
    minDays: 1,
    maxDays: 3,
    enabled: true,
  });
  
  const toggleZone = (zoneId) => {
    setShippingConfig(prev => ({
      ...prev,
      zones: prev.zones.map(z => 
        z.id === zoneId ? { ...z, enabled: !z.enabled } : z
      )
    }));
  };
  
  const openEditZone = (zone) => {
    setEditingZone(zone);
    setZoneForm({
      id: zone.id,
      name: zone.name,
      price: zone.price.toString(),
      minDays: zone.minDays,
      maxDays: zone.maxDays,
      enabled: zone.enabled,
    });
    setShowZoneModal(true);
  };
  
  const openAddZone = () => {
    setEditingZone(null);
    setZoneForm({
      id: `zone-${Date.now()}`,
      name: '',
      price: '',
      minDays: 1,
      maxDays: 3,
      enabled: true,
    });
    setShowZoneModal(true);
  };
  
  const handleSaveZone = () => {
    if (!zoneForm.name || !zoneForm.price) return;
    
    const zoneData = {
      id: zoneForm.id,
      name: zoneForm.name,
      price: parseFloat(zoneForm.price),
      minDays: parseInt(zoneForm.minDays),
      maxDays: parseInt(zoneForm.maxDays),
      enabled: zoneForm.enabled,
    };
    
    setShippingConfig(prev => ({
      ...prev,
      zones: editingZone
        ? prev.zones.map(z => z.id === editingZone.id ? zoneData : z)
        : [...prev.zones, zoneData]
    }));
    
    setShowZoneModal(false);
  };
  
  const handleDeleteZone = (zoneId) => {
    setShippingConfig(prev => ({
      ...prev,
      zones: prev.zones.filter(z => z.id !== zoneId)
    }));
  };
  
  // Se non ci sono prodotti
  if (productCount === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        {/* Header */}
        <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-4 p-4">
              <button 
                onClick={onBack} 
                className="p-2 -ml-2 hover:bg-gray-100 rounded-full"
                data-testid="shipping-config-back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
              <div className="flex-1">
                <h1 className="text-xl font-bold text-gray-900">Costi di Spedizione</h1>
                <p className="text-sm text-gray-500">Configura le opzioni di consegna</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="max-w-screen-2xl mx-auto p-4">
          <div className="bg-white rounded-2xl p-8 text-center">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-10 h-10 text-purple-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nessun prodotto
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Per configurare le spedizioni, crea prima dei prodotti dalla sezione Catalogo.
            </p>
            <button
              onClick={() => onNavigate('office-catalog')}
              className="inline-flex items-center gap-2 bg-emerald-500 text-white px-6 py-3 rounded-xl font-medium hover:bg-emerald-600 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Vai al Catalogo
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-4 p-4">
            <button 
              onClick={onBack} 
              className="p-2 -ml-2 hover:bg-gray-100 rounded-full"
              data-testid="shipping-config-back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Costi di Spedizione</h1>
              <p className="text-sm text-gray-500">{productCount} prodotti nel catalogo</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-screen-2xl mx-auto p-4 space-y-4">
        {/* Delivery Options */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Opzioni di Consegna</h3>
          </div>
          
          <div className="divide-y divide-gray-100">
            {/* Shipping Toggle */}
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Truck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Spedizione</p>
                  <p className="text-sm text-gray-500">Consegna a domicilio</p>
                </div>
              </div>
              <button
                onClick={() => setShippingConfig(prev => ({ ...prev, enableShipping: !prev.enableShipping }))}
                className={`relative w-12 h-7 rounded-full transition-colors ${shippingConfig.enableShipping ? 'bg-emerald-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${shippingConfig.enableShipping ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            
            {/* Pickup Toggle */}
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Ritiro in sede</p>
                  <p className="text-sm text-gray-500">Il cliente ritira presso di te</p>
                </div>
              </div>
              <button
                onClick={() => setShippingConfig(prev => ({ ...prev, enablePickup: !prev.enablePickup }))}
                className={`relative w-12 h-7 rounded-full transition-colors ${shippingConfig.enablePickup ? 'bg-emerald-500' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${shippingConfig.enablePickup ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Free Shipping Threshold */}
        {shippingConfig.enableShipping && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Gift className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Spedizione Gratuita</p>
                <p className="text-sm text-gray-500">Sopra una soglia minima</p>
              </div>
            </div>
            
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="number"
                value={shippingConfig.freeShippingThreshold}
                onChange={(e) => setShippingConfig(prev => ({ ...prev, freeShippingThreshold: parseFloat(e.target.value) || 0 }))}
                placeholder="Soglia per spedizione gratuita"
                className="w-full pl-10 pr-4 py-3 bg-gray-100 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {shippingConfig.freeShippingThreshold > 0 
                ? `Spedizione gratuita per ordini superiori a €${shippingConfig.freeShippingThreshold}`
                : 'Imposta 0 per disabilitare la spedizione gratuita'}
            </p>
          </div>
        )}
        
        {/* Handling Time */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tempo di preparazione ordine
          </label>
          <select
            value={shippingConfig.handlingTime}
            onChange={(e) => setShippingConfig(prev => ({ ...prev, handlingTime: parseInt(e.target.value) }))}
            className="w-full px-4 py-3 bg-gray-100 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none"
          >
            <option value={0}>Stesso giorno</option>
            <option value={1}>1 giorno lavorativo</option>
            <option value={2}>2 giorni lavorativi</option>
            <option value={3}>3 giorni lavorativi</option>
            <option value={5}>5 giorni lavorativi</option>
            <option value={7}>1 settimana</option>
          </select>
        </div>
        
        {/* Shipping Zones */}
        {shippingConfig.enableShipping && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Zone di Spedizione</h3>
              <button
                onClick={openAddZone}
                className="flex items-center gap-1 text-emerald-600 text-sm font-medium hover:text-emerald-700"
              >
                <Plus className="w-4 h-4" />
                Aggiungi zona
              </button>
            </div>
            
            <div className="divide-y divide-gray-100">
              {shippingConfig.zones.map(zone => {
                const defaultZone = DEFAULT_ZONES.find(z => z.id === zone.id);
                const color = defaultZone?.color || 'gray';
                
                return (
                  <div key={zone.id} className={`p-4 ${!zone.enabled ? 'opacity-50' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => toggleZone(zone.id)}
                          className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${
                            zone.enabled ? `bg-${color}-500 text-white` : 'bg-gray-200 text-gray-400'
                          }`}
                          style={zone.enabled ? { backgroundColor: color === 'emerald' ? '#10b981' : color === 'blue' ? '#3b82f6' : color === 'purple' ? '#8b5cf6' : '#f97316' } : {}}
                        >
                          {zone.enabled && <Check className="w-4 h-4" />}
                        </button>
                        <div>
                          <p className="font-medium text-gray-900">{zone.name}</p>
                          <p className="text-xs text-gray-500">
                            {zone.minDays === zone.maxDays 
                              ? `${zone.minDays} giorni` 
                              : `${zone.minDays}-${zone.maxDays} giorni`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-gray-900">€{zone.price.toFixed(2)}</span>
                        <button
                          onClick={() => openEditZone(zone)}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                          <Edit3 className="w-4 h-4 text-gray-500" />
                        </button>
                        {!['local', 'regional', 'national'].includes(zone.id) && (
                          <button
                            onClick={() => handleDeleteZone(zone.id)}
                            className="p-2 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Info Box */}
        <div className="bg-blue-50 rounded-2xl p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">Come funzionano le zone di spedizione</p>
            <p>
              Ogni zona ha un costo e un tempo di consegna stimato. 
              Il cliente vedrà automaticamente la zona corretta in base al suo indirizzo.
              I tempi includono il tempo di preparazione impostato sopra.
            </p>
          </div>
        </div>
        
        {/* Save Button */}
        <button
          className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2"
        >
          <Save className="w-5 h-5" />
          Salva Configurazione
        </button>
      </div>
      
      {/* Zone Edit Modal */}
      {showZoneModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center">
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-bold">
                {editingZone ? 'Modifica Zona' : 'Nuova Zona'}
              </h3>
              <button 
                onClick={() => setShowZoneModal(false)} 
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome zona *</label>
                <input
                  type="text"
                  value={zoneForm.name}
                  onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })}
                  placeholder="Es. Isole, Sud Italia..."
                  className="w-full px-4 py-3 bg-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Costo spedizione *</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="number"
                    value={zoneForm.price}
                    onChange={(e) => setZoneForm({ ...zoneForm, price: e.target.value })}
                    placeholder="0.00"
                    step="0.01"
                    className="w-full pl-9 pr-4 py-3 bg-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Giorni min</label>
                  <input
                    type="number"
                    value={zoneForm.minDays}
                    onChange={(e) => setZoneForm({ ...zoneForm, minDays: e.target.value })}
                    min="1"
                    className="w-full px-4 py-3 bg-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Giorni max</label>
                  <input
                    type="number"
                    value={zoneForm.maxDays}
                    onChange={(e) => setZoneForm({ ...zoneForm, maxDays: e.target.value })}
                    min="1"
                    className="w-full px-4 py-3 bg-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
                <div>
                  <p className="font-medium text-gray-900">Zona attiva</p>
                  <p className="text-sm text-gray-500">Mostra questa opzione ai clienti</p>
                </div>
                <button
                  onClick={() => setZoneForm({ ...zoneForm, enabled: !zoneForm.enabled })}
                  className={`relative w-12 h-7 rounded-full transition-colors ${zoneForm.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${zoneForm.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-100">
              <button
                onClick={handleSaveZone}
                disabled={!zoneForm.name || !zoneForm.price}
                className="w-full bg-emerald-500 text-white py-4 rounded-xl font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                {editingZone ? 'Salva modifiche' : 'Aggiungi zona'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingConfigPage;
